import difference from 'lodash-es/difference';
import isEqual from 'lodash-es/isEqual';

import { isNullOrUndefined } from '@app/util/util';
import {
  BusinessRulesByBookingType, BookingPaxTypeDocumentProps, DocumentAssetState,
  EnabledPaxDocumentsRelationBetweenAirlineAndProviders,
  EnabledPaxDocumentsConditions
} from '@app/entities/booking-documents';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { BookingDocumentPaxIdType } from '@app/entities/booking-pax-documents-id';
import {
  EnabledBookingDocumentsByProviderValues, EnabledBookingDocumentsByAirlineValues,
  EnabledBookingDocumentsByCondition, IgnoreEnabledBookingDocumentsByCondition
} from '@app/entities/booking-enabled-documents-by';
import { PaxDocumentResponse, TravelerPaxResponse } from '@pax/entities/traveler-pax';

export function getTravelerDocumentsByBookingType(
  documentAssets: DocumentAssetState, travelerType: TravelerTypes, bookingType: TravelBookingType
): BookingPaxTypeDocumentProps[] {
  const defaultDocuments = documentAssets?.PAX && documentAssets.PAX[travelerType] ? documentAssets.PAX[travelerType] : [];
  switch (bookingType) {
    case TravelBookingType.HOTELS:
    case TravelBookingType.PACKAGE_AIR:
      if (travelerType === TravelerTypes.ADULTS) {
        return defaultDocuments.filter(_document => _document.TYPE !== BookingDocumentPaxIdType.ID_CARD);
      } else return defaultDocuments;
    default:
      return defaultDocuments;
  }
}

export function isEnabledPaxDocumentModule(
  config: BusinessRulesByBookingType, isAnInternationalTrip: boolean, productCode: string, productProvider: string
): boolean {
  const enabledConfig = config?.ENABLED_PAX_MODULE_DOCUMENTS || [];
  const enabled = enabledConfig.map(condition => {
    if (
      condition?.IGNORE_RULE_AND_DISABLED_WHEN?.length > 0 && 
      disabledModuleWhenTheRuleHasBeenIgnored(condition, isAnInternationalTrip, productCode)
    ) {
      return false;
    } else {
      switch (condition?.TYPE) {
        case EnabledBookingDocumentsByCondition.ENABLED_DOCUMENTS_MODULE:
          return true;
        case EnabledBookingDocumentsByCondition.ENABLED_DOCUMENTS_BY_AIRLINE:
          const valuesByAirline = (condition.VALUES as EnabledBookingDocumentsByAirlineValues[])
            .map(value => value.toUpperCase());
          const airlineValue = productCode?.toUpperCase();
          return valuesByAirline.includes(airlineValue);
        case EnabledBookingDocumentsByCondition.ENABLED_DOCUMENTS_BY_PROVIDER:
          const valuesByProvider = (condition.VALUES as EnabledBookingDocumentsByProviderValues[])
            .map(value => value.toUpperCase());
          const providerValue = productProvider?.toUpperCase();
          return valuesByProvider.includes(providerValue);
        case EnabledBookingDocumentsByCondition.ENABLED_DOCUMENTS_ONLY_INTERNATIONAL_TRIP:
          return !!isAnInternationalTrip;
        case EnabledBookingDocumentsByCondition.ENABLED_DOCUMENTS_BY_AIRLINE_AND_PROVIDER:
          const values = (condition.VALUES as EnabledPaxDocumentsRelationBetweenAirlineAndProviders);
          if (isValidProductCode(productCode, values) && productProvider) {
            const relationship = fromEntriesObjectUppercase(values);
            const providers = relationship[productCode.toUpperCase()];
            if (providers?.length) {
              return providers.includes(productProvider.toUpperCase());
            } else {
              return false;
            }
          } else {
            return false;
          }
        default:
          return false;
      }
    }
  });
  return enabled.reduce((sum, next) => sum || next, false);
}

function disabledModuleWhenTheRuleHasBeenIgnored(
  condition: EnabledPaxDocumentsConditions, isAnInternationalTrip: boolean, productCode: string
): boolean {
  const ignore = condition?.IGNORE_RULE_AND_DISABLED_WHEN?.map(disabledWhen => {
    switch (disabledWhen?.TYPE) {
      case IgnoreEnabledBookingDocumentsByCondition.AIRLINE_IS_EQUAL_TO:
        return disabledWhen?.VALUE?.some(airlineCode => productCode?.toUpperCase() === airlineCode?.toUpperCase());
      case IgnoreEnabledBookingDocumentsByCondition.IS_INTERNATIONAL_TRIP:
        return !!isAnInternationalTrip;
      default:
        return false;
    }
  });
  return ignore.reduce((sum, next) => sum || next, false);
}

export function isCreatedNewPaxMapperByForm(
  hasInfo: boolean, documents: BookingPaxTypeDocumentProps[],
  pax: TravelerPaxResponse, registerDocuments: PaxDocumentResponse[]
): boolean {
  if (hasInfo) {
    if (registerDocuments.length) {
      if (isNullOrUndefined(documents.find(doc => doc.DOCUMENT_ID === pax?.configuration_document_id))) return true;
      else {
        const registerIds = registerDocuments.map(register => register.type).sort((a, b) => (a - b));
        const documentsIds = documents.map(doc => doc.DOCUMENT_ID.toString()).sort();
        if (isEqual(documentsIds, registerIds)) return false;
        else {
          const differenteBetween: string[] = difference(documentsIds, registerIds);
          return differenteBetween ? !differenteBetween.length : true;
        }
      }
    } else return false;
  } else return true;
}

function fromEntriesObjectUppercase(values: { [key: string]: string[] }): { [key: string]: string[] } {
  return fromEntries(Object.entries(values).map(([key, value]) => [key.toUpperCase(), value.map(val => val.toUpperCase())]));
}

function fromEntries(entries: [string, string[]][]): { [key: string]: string[] } {
  return entries.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
}

function isValidProductCode(productCode: string, values: { [key: string]: string[] }): boolean {
  const codes = Object.keys(values).map(key => key.toUpperCase());
  return !!productCode && codes.includes(productCode.toUpperCase());
}
